import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RefreshCw, Trash2, Users, X, Building2, Send, Copy } from "lucide-react";
import { toast } from "../../components/ui/Toast";

const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("token")}`,
  "Content-Type": "application/json",
});

const cardCls =
  "rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022]";

const inputCls =
  "w-full p-2.5 rounded-lg bg-black/40 border border-white/20 text-white placeholder-white/30 " +
  "outline-none focus:border-[#00FF99]/60 text-sm";

const primaryBtn =
  "inline-flex items-center gap-2 bg-[#00FF99] text-black font-semibold px-4 py-2 rounded-lg " +
  "hover:bg-[#00cc7a] transition disabled:opacity-50";

const secondaryBtn =
  "inline-flex items-center gap-2 bg-white/5 border border-white/15 text-white/90 px-3 py-2 " +
  "rounded-lg hover:bg-white/10 transition text-sm";

const dangerBtn =
  "inline-flex items-center gap-1 bg-red-500/10 border border-red-500/40 text-red-300 " +
  "px-2.5 py-1.5 rounded-lg hover:bg-red-500/20 transition text-xs";

const resendBtn =
  "inline-flex items-center gap-1 bg-[#00FF99]/10 border border-[#00FF99]/40 text-[#00FF99] " +
  "px-2.5 py-1.5 rounded-lg hover:bg-[#00FF99]/20 transition text-xs disabled:opacity-50 disabled:cursor-not-allowed";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function inviteStatus(invite) {
  if (invite.is_used) return { label: "Used / Superseded", tone: "neutral" };
  try {
    if (new Date(invite.expires_at) < new Date()) {
      return { label: "Expired", tone: "warn" };
    }
  } catch {}
  return { label: "Pending", tone: "ok" };
}

function Modal({ title, onClose, children, wide = false }) {
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className={`${cardCls} ${wide ? "max-w-2xl" : "max-w-md"} w-full p-6`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-[#00FF99]">{title}</h3>
          <button onClick={onClose} className="text-white/40 hover:text-white" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function BulkInviteForm({ orgs, onCancel, onDone }) {
  const [orgId, setOrgId]   = useState(orgs[0]?.id || "");
  const [role, setRole]     = useState("staff");
  const [raw, setRaw]       = useState("");
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");
  const [result, setResult] = useState(null);

  const parseEmails = () =>
    raw
      .split(/[\n,;]+/)
      .map((e) => e.trim().toLowerCase())
      .filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!orgId) { setError("Select an organisation."); return; }
    const emails = parseEmails();
    if (emails.length === 0) { setError("Paste at least one email."); return; }
    if (emails.length > 50) { setError("Maximum 50 invites per batch."); return; }

    setBusy(true);
    try {
      const body = {
        invites: emails.map((email) => ({
          email,
          role,
          org_id: orgId,
          expires_hours: 72,
        })),
      };
      const res = await fetch(`${API_BASE}/api/onboarding/staff/invite/bulk`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.detail || "Bulk invite failed.");
        setBusy(false);
        return;
      }
      setResult(data);
    } catch {
      setError("Network error.");
    } finally {
      setBusy(false);
    }
  };

  if (result) {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-lg bg-[#00FF99]/10 border border-[#00FF99]/30 p-3">
            <div className="text-xs text-white/60">Sent</div>
            <div className="text-2xl text-[#00FF99] font-bold">{result.sent?.length || 0}</div>
          </div>
          <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3">
            <div className="text-xs text-white/60">Failed</div>
            <div className="text-2xl text-red-300 font-bold">{result.failed?.length || 0}</div>
          </div>
        </div>

        {result.failed?.length > 0 && (
          <div>
            <div className="text-xs text-white/60 mb-1">Failures</div>
            <div className="max-h-40 overflow-y-auto border border-white/10 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-white/5 text-white/50">
                  <tr>
                    <th className="text-left px-3 py-2">Email</th>
                    <th className="text-left px-3 py-2">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {result.failed.map((f, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-3 py-2 text-white/80">{f.email}</td>
                      <td className="px-3 py-2 text-red-300">{f.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="flex justify-end pt-2">
          <button onClick={onDone} className={primaryBtn}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-white/60 block mb-1">Organisation</label>
          <select className={inputCls} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— Select —</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name || o.slug || o.id}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-white/60 block mb-1">Role</label>
          <select className={inputCls} value={role} onChange={(e) => setRole(e.target.value)}>
            <option value="staff">staff</option>
            <option value="manager">manager</option>
            <option value="executive">executive</option>
          </select>
        </div>
      </div>

      <div>
        <label className="text-xs text-white/60 block mb-1">
          Emails (one per line, up to 50)
        </label>
        <textarea
          className={inputCls + " font-mono text-xs h-40 resize-y"}
          value={raw}
          onChange={(e) => setRaw(e.target.value)}
          placeholder={"alice@example.com\nbob@example.com\ncharlie@example.com"}
        />
        <p className="text-xs text-white/40 mt-1">
          Parsed: {parseEmails().length} email{parseEmails().length === 1 ? "" : "s"}
        </p>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onCancel} className={secondaryBtn}>Cancel</button>
        <button type="submit" disabled={busy} className={primaryBtn}>
          {busy ? "Sending…" : "Send Invites"}
        </button>
      </div>
    </form>
  );
}

export default function SuperadminStaffInvites() {
  const [orgs, setOrgs]       = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [filterOrg, setFilterOrg] = useState("");  // "" = all
  const [showBulk, setShowBulk]   = useState(false);
  const [resendBusyId, setResendBusyId]   = useState(null);     // invite.id currently resending
  const [resendError, setResendError]     = useState({});       // { [invite.id]: "msg" }
  const [resendResult, setResendResult]   = useState(null);     // { email, invite_url, expires_at }
  const [toast, setToast]                 = useState(null);     // { message, tone }

  // auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const copyToClipboard = useCallback(async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast({ message: "URL copied to clipboard", tone: "ok" });
    } catch {
      setToast({ message: "Could not copy. Select and copy manually.", tone: "warn" });
    }
  }, []);

  const loadOrgs = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/onboarding/orgs`, { headers: authHeaders() });
      if (!res.ok) return;
      const data = await res.json();
      setOrgs(Array.isArray(data) ? data : []);
    } catch {}
  }, []);

  const loadInvites = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url = filterOrg
        ? `${API_BASE}/api/onboarding/staff/invites?org_id=${encodeURIComponent(filterOrg)}`
        : `${API_BASE}/api/onboarding/staff/invites`;
      const res = await fetch(url, { headers: authHeaders() });
      const data = await res.json().catch(() => []);
      if (!res.ok) {
        setError(data?.detail || "Failed to load invites.");
        setInvites([]);
      } else {
        setInvites(Array.isArray(data) ? data : []);
      }
    } catch {
      setError("Network error.");
    } finally {
      setLoading(false);
    }
  }, [filterOrg]);

  useEffect(() => { loadOrgs(); }, [loadOrgs]);
  useEffect(() => { loadInvites(); }, [loadInvites]);

  const orgLookup = useMemo(() => {
    const m = {};
    for (const o of orgs) m[o.id] = o;
    return m;
  }, [orgs]);

  const cancelInvite = async (invite) => {
    if (invite.is_used) return;
    if (!window.confirm(`Cancel invite for ${invite.email}?`)) return;
    try {
      const res = await fetch(
        `${API_BASE}/api/onboarding/staff/invite/${invite.id}`,
        { method: "DELETE", headers: authHeaders() }
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "Failed to cancel invite.");
        return;
      }
      // optimistic remove/flag
      setInvites((rows) =>
        rows.map((r) => (r.id === invite.id ? { ...r, is_used: true } : r))
      );
      toast.success("Invite cancelled");
    } catch {
      toast.error("Network error.");
    }
  };

  const resendInvite = async (invite) => {
    setResendBusyId(invite.id);
    setResendError((m) => ({ ...m, [invite.id]: "" }));
    try {
      const res = await fetch(
        `${API_BASE}/api/onboarding/staff/invite/${invite.id}/resend`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setResendError((m) => ({ ...m, [invite.id]: data.detail || "Failed to resend invite." }));
        return;
      }
      setResendResult({
        email:       data.email,
        invite_url:  data.invite_url,
        expires_at:  data.expires_at,
      });
      setToast({ message: "Invite resent successfully", tone: "ok" });
      loadInvites();
    } catch {
      setResendError((m) => ({ ...m, [invite.id]: "Network error." }));
    } finally {
      setResendBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <button onClick={loadInvites} className={secondaryBtn} title="Refresh">
          <RefreshCw size={16} />
        </button>
        <button onClick={() => setShowBulk(true)} className={primaryBtn}>
          <Users size={16} /> Bulk Invite
        </button>
      </div>

        <div className={`${cardCls} p-4 mb-4 flex flex-wrap items-center gap-3`}>
          <label className="text-xs text-white/60 uppercase tracking-wider">
            Filter by organisation
          </label>
          <select
            className={inputCls + " max-w-xs"}
            value={filterOrg}
            onChange={(e) => setFilterOrg(e.target.value)}
          >
            <option value="">All organisations</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>{o.name || o.slug || o.id}</option>
            ))}
          </select>
        </div>

        <div className={`${cardCls} overflow-hidden`}>
          {loading ? (
            <div className="p-8 text-center text-white/60">Loading invites…</div>
          ) : error ? (
            <div className="p-8 text-center text-red-400">{error}</div>
          ) : invites.length === 0 ? (
            <div className="p-8 text-center text-white/60">No invites found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-white/5 text-white/60 text-xs uppercase">
                  <tr>
                    <th className="text-left px-4 py-3">Email</th>
                    <th className="text-left px-4 py-3">Role</th>
                    <th className="text-left px-4 py-3">Organisation</th>
                    <th className="text-left px-4 py-3">Expires</th>
                    <th className="text-left px-4 py-3">Status</th>
                    <th className="text-right px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invites.map((inv) => {
                    const s = inviteStatus(inv);
                    const org = orgLookup[inv.org_id];
                    const toneCls =
                      s.tone === "ok"
                        ? "bg-[#00FF99]/10 text-[#00FF99] border-[#00FF99]/30"
                        : s.tone === "warn"
                        ? "bg-yellow-500/10 text-yellow-300 border-yellow-500/30"
                        : "bg-white/5 text-white/50 border-white/10";
                    return (
                      <tr key={inv.id} className="border-t border-white/5 hover:bg-white/[0.03]">
                        <td className="px-4 py-3 text-white">{inv.email}</td>
                        <td className="px-4 py-3 text-white/70 font-mono text-xs">{inv.role}</td>
                        <td className="px-4 py-3 text-white/70">
                          {org?.name || org?.slug || <span className="text-white/40 font-mono text-xs">{inv.org_id}</span>}
                        </td>
                        <td className="px-4 py-3 text-white/60">{formatDate(inv.expires_at)}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs border ${toneCls}`}>
                            {s.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2 flex-wrap">
                            <button
                              onClick={() => resendInvite(inv)}
                              disabled={resendBusyId === inv.id}
                              className={resendBtn}
                              title="Issue a fresh invite (supersedes this one)"
                            >
                              <Send size={12} /> {resendBusyId === inv.id ? "Resending…" : "Resend"}
                            </button>
                            <button
                              onClick={() => cancelInvite(inv)}
                              disabled={inv.is_used}
                              className={dangerBtn + " disabled:opacity-40 disabled:cursor-not-allowed"}
                              title={inv.is_used ? "Already used or cancelled" : "Cancel invite"}
                            >
                              <Trash2 size={12} /> Cancel
                            </button>
                          </div>
                          {resendError[inv.id] && (
                            <div className="text-xs text-red-400 mt-1 text-right">
                              {resendError[inv.id]}
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      {showBulk && (
        <Modal title="Bulk Invite" onClose={() => setShowBulk(false)} wide>
          <BulkInviteForm
            orgs={orgs}
            onCancel={() => setShowBulk(false)}
            onDone={() => { setShowBulk(false); loadInvites(); }}
          />
        </Modal>
      )}

      {resendResult && (
        <Modal title="Invite Resent" onClose={() => setResendResult(null)}>
          <div className="space-y-3">
            <p className="text-sm text-white/70">
              A fresh invite URL has been issued for{" "}
              <span className="text-white font-medium">{resendResult.email}</span>.
              The previous invite is now superseded.
            </p>
            <div>
              <label className="text-xs text-white/60 uppercase tracking-wider block mb-1">
                Invite URL
              </label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={resendResult.invite_url}
                  className={inputCls + " font-mono text-xs"}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  onClick={() => copyToClipboard(resendResult.invite_url)}
                  className={primaryBtn}
                  title="Copy to clipboard"
                >
                  <Copy size={14} /> Copy
                </button>
              </div>
            </div>
            <p className="text-xs text-white/40">
              Expires {formatDate(resendResult.expires_at)}
            </p>
            <div className="flex justify-end pt-2">
              <button onClick={() => setResendResult(null)} className={secondaryBtn}>
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {toast && (
        <div
          className={
            "fixed bottom-6 right-6 z-[60] px-4 py-3 rounded-lg shadow-xl border " +
            (toast.tone === "ok"
              ? "bg-[#00FF99]/15 border-[#00FF99]/40 text-[#00FF99]"
              : "bg-yellow-500/15 border-yellow-500/40 text-yellow-200")
          }
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
}
